import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { useLanguage } from '@/contexts/LanguageContext';
import { RotateCw, ZoomIn, ZoomOut, Move } from 'lucide-react';

interface ImageCropperProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  onCropComplete: (croppedBlob: Blob) => void;
}

const ImageCropper = ({ isOpen, onClose, imageUrl, onCropComplete }: ImageCropperProps) => {
  const { t } = useLanguage();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [scale, setScale] = useState([1]);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  // Reset everything when dialog opens
  useEffect(() => {
    if (isOpen) {
      console.log('Dialog opened, loading image:', imageUrl);
      setScale([1]);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
      setImageLoaded(false);
      setIsDragging(false);
      
      // Force load image
      if (imageRef.current) {
        const img = imageRef.current;
        img.onload = () => {
          console.log('Image loaded successfully');
          setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
          setImageLoaded(true);
          setTimeout(drawCanvas, 100); // Give a moment to render
        };
        img.onerror = (e) => {
          console.error('Image failed to load:', e);
        };
        img.src = imageUrl;
      }
    }
  }, [isOpen, imageUrl]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image || !imageLoaded) {
      console.log('Canvas draw conditions not met:', { canvas: !!canvas, image: !!image, loaded: imageLoaded });
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const canvasSize = 300;
    canvas.width = canvasSize;
    canvas.height = canvasSize;

    // Clear canvas with light background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, canvasSize, canvasSize);

    const centerX = canvasSize / 2;
    const centerY = canvasSize / 2;
    const cropRadius = canvasSize / 2 - 20;

    ctx.save();

    // Apply transformations
    ctx.translate(centerX + position.x, centerY + position.y);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(scale[0], scale[0]);

    // Calculate image size to fit in crop circle
    const imageAspect = image.naturalWidth / image.naturalHeight;
    const maxSize = cropRadius * 1.8; // Make it bigger to fill the circle better
    
    let drawWidth, drawHeight;
    if (imageAspect > 1) {
      // Landscape
      drawWidth = maxSize;
      drawHeight = maxSize / imageAspect;
    } else {
      // Portrait or square  
      drawHeight = maxSize;
      drawWidth = maxSize * imageAspect;
    }

    // Draw the actual image
    ctx.drawImage(image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
    ctx.restore();

    // Create crop overlay (darken outside circle)
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvasSize, canvasSize);
    
    // Cut out the crop circle
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(centerX, centerY, cropRadius, 0, 2 * Math.PI);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
    ctx.restore();

    // Draw crop circle border
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(centerX, centerY, cropRadius, 0, 2 * Math.PI);
    ctx.stroke();
  };

  // Redraw when any parameter changes
  useEffect(() => {
    if (imageLoaded) {
      drawCanvas();
    }
  }, [scale, rotation, position, imageLoaded]);

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setIsDragging(true);
    setDragStart({ x: x - position.x, y: y - position.y });
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setPosition({
      x: x - dragStart.x,
      y: y - dragStart.y
    });
    e.preventDefault();
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch events for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!canvasRef.current || e.touches.length !== 1) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    setIsDragging(true);
    setDragStart({ x: x - position.x, y: y - position.y });
    e.preventDefault();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !canvasRef.current || e.touches.length !== 1) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    setPosition({
      x: x - dragStart.x,
      y: y - dragStart.y
    });
    e.preventDefault();
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Crop function
  const handleCrop = () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image || !imageLoaded) return;

    // Create final crop canvas
    const finalSize = 400;
    const finalCanvas = document.createElement('canvas');
    const finalCtx = finalCanvas.getContext('2d');
    
    if (!finalCtx) return;
    
    finalCanvas.width = finalSize;
    finalCanvas.height = finalSize;

    const centerX = finalSize / 2;
    const centerY = finalSize / 2;
    const scaleFactor = finalSize / 300; // Scale from display to final size

    finalCtx.save();
    finalCtx.translate(centerX + position.x * scaleFactor, centerY + position.y * scaleFactor);
    finalCtx.rotate((rotation * Math.PI) / 180);
    finalCtx.scale(scale[0], scale[0]);

    // Calculate final image size
    const imageAspect = image.naturalWidth / image.naturalHeight;
    const maxSize = (finalSize / 2 - 20) * 1.8;
    
    let drawWidth, drawHeight;
    if (imageAspect > 1) {
      drawWidth = maxSize;
      drawHeight = maxSize / imageAspect;
    } else {
      drawHeight = maxSize;
      drawWidth = maxSize * imageAspect;
    }

    finalCtx.drawImage(image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
    finalCtx.restore();

    // Apply circular mask
    finalCtx.globalCompositeOperation = 'destination-in';
    finalCtx.beginPath();
    finalCtx.arc(centerX, centerY, finalSize / 2, 0, 2 * Math.PI);
    finalCtx.fill();

    // Convert to blob
    finalCanvas.toBlob((blob) => {
      if (blob) {
        onCropComplete(blob);
      }
    }, 'image/png', 0.9);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {t('crop_profile_picture', 'اقتصاص الصورة الشخصية', 'Crop Profile Picture')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Preloaded image */}
          <img
            ref={imageRef}
            src={imageUrl}
            alt="Original"
            className="hidden"
            style={{ maxWidth: '100px', maxHeight: '100px' }}
          />
          
          {/* Show loading state */}
          {!imageLoaded && (
            <div className="flex justify-center items-center h-80">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-sm text-muted-foreground">Loading image...</p>
              </div>
            </div>
          )}
          
          {/* Canvas container */}
          {imageLoaded && (
            <div ref={containerRef} className="flex justify-center">
              <canvas
                ref={canvasRef}
                className="border-2 border-gray-300 rounded-lg cursor-move bg-gray-100"
                style={{ 
                  maxWidth: '100%', 
                  height: 'auto',
                  touchAction: 'none'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              />
            </div>
          )}
          
          {/* Controls */}
          {imageLoaded && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ZoomOut className="h-4 w-4" />
                <Slider
                  value={scale}
                  onValueChange={setScale}
                  min={0.5}
                  max={3}
                  step={0.1}
                  className="flex-1"
                />
                <ZoomIn className="h-4 w-4" />
              </div>
              
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRotation(r => r - 90)}
                >
                  <RotateCw className="h-4 w-4 scale-x-[-1]" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRotation(r => r + 90)}
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPosition({ x: 0, y: 0 })}
                >
                  <Move className="h-4 w-4 mr-1" />
                  {t('center', 'توسيط', 'Center')}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('cancel', 'إلغاء', 'Cancel')}
          </Button>
          <Button onClick={handleCrop} disabled={!imageLoaded}>
            {t('apply_crop', 'تطبيق القص', 'Apply Crop')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropper;
